import React, { useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import Icon from '../styles/Icon.style'
import Font from '../styles/Font'
import SearchBar, { SearchBarContainer } from '../SearchBar'
import OnClickOutsideWrapper from '../OnClickOutsideWrapper'
import useScrollYWithSelector from '../Dropdown/useDashboardScroll'

type Props = {
    label: string
    options: { value: string; label: string }[]
    value: string
    onChange: (value: string) => void
    dropdownGap?: number
    placeholder?: string
}

// import SingleDropdown from 'plugin-smart-mobility-utils/shared/SingleDropdown'
const SingleDropdown = ({ onChange, options = [], value, label, dropdownGap = 0, ...props }: Props): JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement>(null)

    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [top, setTop] = useState(0)

    const toggle = (): void => {
        setOpen(prev => !prev)
    }

    const onSelect = (value: string): void => {
        onChange?.(value)
        setOpen(false)
    }

    const scrollPosition = useScrollYWithSelector('.dashboard-content')

    React.useEffect(() => {
        if (open) {
            const container = containerRef.current
            const boundingClientRect = container.getBoundingClientRect()
            const dropdownTop = boundingClientRect?.y - 0.02 * scrollPosition + container.clientHeight + dropdownGap

            setTop(dropdownTop)

            setSearch('')
        }
    }, [open, scrollPosition, dropdownGap])

    const filteredOptions = React.useMemo(() => {
        return options.filter(
            option =>
                // TODO check other scenarios
                String(option.label)?.toLowerCase().includes(String(search)?.toLowerCase()) ||
                String(option.value)?.toLowerCase().includes(String(search)?.toLowerCase())
        )
    }, [options, search])

    return (
        <SingleDropdownContainer ref={containerRef} className="flex-column" open={open}>
            {label && (
                <Font
                    className="dropdown-label"
                    size="16px"
                    color="var(--gray-600)"
                    fontFamily="SourceSansPro-Regular"
                    weight={600}
                >
                    {i18n(label)}
                </Font>
            )}
            <button className="singleDropdown-button flex-row align-center" onClick={toggle}>
                <Font size="16px" fontFamily="SourceSansPro-Semibold" className="value-field">
                    {value ?? props.placeholder ?? i18n('Select')}
                </Font>
                <Icon className={open ? 'fas fa-caret-up' : 'fas fa-caret-down'} />
            </button>

            {open && (
                <OnClickOutsideWrapper isOpen={open} onClose={() => setOpen(false)} excludeId={'singleDropdown-button'}>
                    <FloatingOptions className="floating-options flex-column" style={{ top: `${top}px` }}>
                        <SearchBar value={search} onChange={setSearch} options={{ border: false }} />
                        {filteredOptions.length === 0 && <Font className="empty-options">{i18n('Empty options')}</Font>}
                        <ul className="list-container">
                            {filteredOptions
                                .filter(option => option.label.toLowerCase().includes(search.toLowerCase()))
                                .map(option => {
                                    const selected = option.value === value

                                    return (
                                        <Li
                                            className="flex-row align-center"
                                            key={option.value}
                                            onClick={() => onSelect(option.value)}
                                            selected={selected}
                                        >
                                            <Font>{option.label}</Font>
                                        </Li>
                                    )
                                })}
                        </ul>
                    </FloatingOptions>
                </OnClickOutsideWrapper>
            )}
        </SingleDropdownContainer>
    )
}

export default SingleDropdown

export const SingleDropdownContainer = styled.div<{ open: boolean }>`
    position: relative;
    width: fit-content;
    gap: 6px;

    .flex-row {
        display: flex;
        flex-direction: row;
    }

    .flex-column {
        display: flex;
        flex-direction: column;
    }

    .align-center {
        align-items: center;
    }

    .dropdown-label {
        text-transform: uppercase;
        margin-left: 8px;
    }

    .singleDropdown-button {
        width: fit-content;
        padding: 8px;
        gap: 8px;

        color: var(--blue-500);
        background-color: transparent;
        border: none;
        border-radius: 3px;
        font-size: 20px;

        :hover {
            background: var(--gray-200);
        }

        .value-field {
            min-width: max-content;
        }

        ${({ open }) => open && `background: var(--blue-200);`}
    }
`

const FloatingOptions = styled.div`
    position: fixed;
    width: 260px;
    z-index: 99;
    background-color: var(--gray-0);
    border: 1px solid var(--gray-300);

    gap: 8px;

    ${SearchBarContainer} {
        border-bottom: 1px solid var(--gray-300);
    }

    .list-container {
        max-height: 160px;
        padding: 0;
        margin: 0;
        overflow-y: auto;
    }

    .empty-options {
        padding: 16px;
        text-align: center;
        font-size: 12px;
        color: var(--gray-600);
    }
`

const Li = styled.li`
    height: 32px;
    padding: 4px 8px;
    cursor: pointer;

    ${({ selected }) => selected && `background-color: var(--gray-200);`}

    :hover {
        background-color: var(--blue-200);
    }
`
